import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, Observable } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import * as fromPrefs from '../../store';
import * as fromRouting from '../../../core/store/routing';
import * as fromAuth from 'src/app/modules/authentication/store/auth';
import { ProfilePreferences } from '../../store/notification-prefs.model';
import { CommunicationsApiService } from '@app/modules/communications/store/communications/communications-api.service';
import { Dataset, Document, View, ViewControlType } from '@app/modules/core/store/models';
import { NotificationPrefsApiService } from '../../store/notification-prefs-api.service';
import { cloneDeep } from 'lodash-es';

@Component({
  selector: 'app-preferences',
  templateUrl: './preferences.component.html',
  styleUrls: ['./preferences.component.scss'],
  standalone: false
})
export class PreferencesComponent implements OnInit, OnDestroy {
  form: FormGroup;
  destroy$ = new Subject<void>();
  supportedUnits = ['MILES', 'KILOMETERS'];
  tabs: string[] = [];
  selectedTabIndex = 0;
  profilePreferences: ProfilePreferences;
  hasCancel: boolean;
  editProfileActive: boolean;
  registerEmailActive: boolean;
  emailField: string;
  registerPhoneActive: boolean;
  phoneField: string;
  tokenChecker$: Observable<number>;
  isUserAccountLinked: boolean;
  isDesktop = window.innerWidth >= 1180;
  // temporary fix for mobile browser issues
  pageHeight = window.innerHeight - 72;
  isHorizonApp: boolean = false;
  availableChannels: string[];
  dataset: Dataset;
  document: Document;
  editView: View;
  documentChanges: any = {};

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    const w = event.target as Window;
    this.isDesktop = w.innerWidth >= 1180;
    this.pageHeight = w.innerHeight - 72;
  }

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private routerStore: Store<fromRouting.State>,
    private translateService: TranslateService,
    private commApiService: CommunicationsApiService,
    private prefsApiService: NotificationPrefsApiService
  ) {}

  showRegisterPhone$ = this.store.select(fromPrefs.selectShowRegisterPhone);
  showRegisterWhatsApp$ = this.store.select(fromPrefs.selectShowRegisterWhatsApp);

  ngOnInit(): void {
    // temporary fix for mobile browser issues
    document.body.style.minHeight = '0';

    this.tabs.push(this.translateService.instant('NOTIFICATION-PREFERENCES.TAB-CONTACT-INFO'));
    this.tabs.push(this.translateService.instant('NOTIFICATION-PREFERENCES.TAB-PREFERENCES'));

    this.loadAvailableChannels();

    this.form = this.fb.group({
      uid: [],
      user_profile_uid: [],
      display_name: [],
      avatar_image: [],
      culture_code: [],
      use_24h_time: [],
      time_zone: [],
      distance_unit: [],
      date_time_format: [],
      work_email: [],
      work_phone: [],
      personal_email: [],
      personal_phone: [],
      slack_address: [],
      teams_address: [],
      whatsapp_address: [],
      telegram_address: [],
      signal_address: [],
      do_not_disturb_from_time: [],
      do_not_disturb_to_time: [],
      use_work_email_extreme: [],
      use_work_email_high: [],
      use_work_email_moderate: [],
      use_work_email_low: [],
      use_work_text_extreme: [],
      use_work_text_high: [],
      use_work_text_moderate: [],
      use_work_text_low: [],
      use_work_phone_call_extreme: [],
      use_work_phone_call_high: [],
      use_work_phone_call_moderate: [],
      use_work_phone_call_low: [],
      use_personal_email_extreme: [],
      use_personal_email_high: [],
      use_personal_email_moderate: [],
      use_personal_email_low: [],
      use_personal_text_extreme: [],
      use_personal_text_high: [],
      use_personal_text_moderate: [],
      use_personal_text_low: [],
      use_personal_phone_call_extreme: [],
      use_personal_phone_call_high: [],
      use_personal_phone_call_moderate: [],
      use_personal_phone_call_low: [],
      use_slack_extreme: [],
      use_slack_high: [],
      use_slack_moderate: [],
      use_slack_low: [],
      use_teams_extreme: [],
      use_teams_high: [],
      use_teams_moderate: [],
      use_teams_low: [],
      use_telegram_extreme: [],
      use_telegram_high: [],
      use_telegram_moderate: [],
      use_telegram_low: [],
      use_signal_extreme: [],
      use_signal_high: [],
      use_signal_moderate: [],
      use_signal_low: [],
      use_whats_app_extreme: [],
      use_whats_app_high: [],
      use_whats_app_moderate: [],
      use_whats_app_low: []
    });

    this.routerStore
      .select(fromRouting.selectQueryParams)
      .pipe(take(1))
      .subscribe(params => {
        this.isHorizonApp = params?.app === 'Horizon';
      });

    this.store
      .select(fromAuth.selectUserContext)
      .pipe(takeUntil(this.destroy$))
      .subscribe(userContext => {
        this.hasCancel = userContext?.defaultRoute !== 'update-preferences';
        this.store.dispatch(fromPrefs.loadAvailableLanguages({ organizationUId: userContext.organizationUId }));
      });

    this.store
      .select(fromPrefs.selectPreferences)
      .pipe(takeUntil(this.destroy$))
      .subscribe(prefs => {
        if (!!prefs) {
          this.form.patchValue(prefs);

          this.document = prefs as Document;
          const datasetUId = this.document['dataset_uid'];

          if (!!datasetUId) {
            this.prefsApiService
              .getNotificationPreferencesDataset(datasetUId)
              .pipe(take(1))
              .subscribe(res => {
                this.dataset = res;
                this.editView = this.getDocumentFieldsView(prefs);
              });
          }

          if (!!prefs.do_not_disturb_from_time && typeof prefs.do_not_disturb_from_time == 'string') {
            this.form.patchValue({ do_not_disturb_from_time: parseInt(prefs.do_not_disturb_from_time) });
          }

          if (!!prefs.do_not_disturb_to_time && typeof prefs.do_not_disturb_to_time == 'string') {
            this.form.patchValue({ do_not_disturb_to_time: parseInt(prefs.do_not_disturb_to_time) });
          }

          this.isUserAccountLinked = !!prefs.user_profile_uid && prefs.user_profile_uid !== '00000000-0000-0000-0000-000000000000';
        }
      });
  }

  private getDocumentFieldsView(prefs: ProfilePreferences): View {
    let editView = this.dataset?.workflow?.views?.find(f => f.name === 'prefs');
    if (!editView || editView?.controls.length == 0) {
      editView = this.dataset?.workflow?.views?.find(f => f.name === 'detail');
    }

    if (!!!editView) {
      return null;
    }

    const updatedView = cloneDeep(editView);

    if (updatedView?.controls?.some(c => c.controlType === ViewControlType.ViewControlContainer)) {
      const containers = updatedView?.controls?.filter(c => c.controlType === ViewControlType.ViewControlContainer);
      containers.forEach(c => {
        Object.keys(this.form.controls).forEach(f => {
          const field = this.dataset?.schema?.fields?.find(df => df.systemName === f);
          if (!!field) {
            const index = c.controls.findIndex(c => c.dataSetFieldUId === field.uId);
            if (index > -1) {
              c.controls.splice(index);
            }
          }
        });
      });
    } else {
      Object.keys(this.form.controls).forEach(f => {
        const field = this.dataset?.schema?.fields?.find(df => df.systemName === f);
        if (!!field) {
          const index = updatedView.controls.findIndex(c => c.dataSetFieldUId === field.uId);
          if (index > -1) {
            updatedView.controls.splice(index);
          }
        }
      });
    }

    return updatedView;
  }

  private loadAvailableChannels() {
    this.availableChannels = [];
    this.commApiService.getOrganizationChannelUIdsOnly().subscribe(res => {
      res.forEach(c => this.availableChannels.push(c));
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  tabChanged(index: number) {
    this.selectedTabIndex = index;
  }

  documentChanged(data: any): void {
    this.documentChanges = data;
  }

  saveClick() {
    const preferences = { ...this.form.value, ...(this.documentChanges ?? {}) };

    this.store.dispatch(fromPrefs.updateNotificationPreferences({ preferences, close: this.hasCancel }));
  }

  editProfile() {
    this.editProfileActive = true;
  }

  editProfileComplete(preferences: ProfilePreferences) {
    this.form.patchValue(preferences);
    this.editProfileActive = false;
  }

  editProfileClose() {
    this.editProfileActive = false;
  }

  cancelClick() {
    window.top.close();
  }
}
